/**
 * JobQuickCreateDialog - Minimal dialog for quick job creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Title, Department, Location Type)
 * - Redirects to detail page after creation for full editing
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { CompanyPicker, DialogForm } from '../../../shared/components/common';
import { DepartmentPicker } from './DepartmentPicker';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useCreateJob, JobCreate } from '../hooks/useJobs';
import { useHiringPipelines } from '../hooks/usePipelines';
import type { JobPosting } from '../services/recruitingApi';

interface JobQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when job is created (before navigation) */
  onCreated?: (job: JobPosting) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
  /** Pre-fill the title field (e.g., from picker search text) */
  defaultTitle?: string;
}

interface QuickCreateFormData {
  title: string;
  description: string;
  department_id?: string;
  location_type: 'onsite' | 'remote' | 'hybrid';
  company_id: string | null;
}

const LOCATION_TYPES = ['onsite', 'remote', 'hybrid'] as const;

export function JobQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
  defaultTitle = '',
}: JobQuickCreateDialogProps) {
  const { t } = useTranslation(['recruiting', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateJob();
  const { handleMutationError } = useApiError({ showToast: false });
  const { data: pipelinesData } = useHiringPipelines();

  // Get default pipeline
  const defaultPipeline = pipelinesData?.find((p) => p.is_default) || pipelinesData?.[0];

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      title: '',
      description: '',
      department_id: undefined,
      location_type: 'onsite',
      company_id: null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: defaultTitle,
        description: '',
        department_id: undefined,
        location_type: 'onsite',
        company_id: null,
      });
    }
  }, [open, defaultTitle, reset]);

  const onSubmit = async (data: QuickCreateFormData) => {
    if (!defaultPipeline) {
      return; // Should not happen if pipelines are loaded
    }

    const createData: JobCreate = {
      title: data.title,
      description: data.description || undefined,
      department_id: data.department_id || undefined,
      location_type: data.location_type,
      pipeline_id: defaultPipeline.id,
      company_id: data.company_id || undefined,
    };

    try {
      const newJob = await createMutation.mutateAsync(createData);

      onCreated?.(newJob);
      onClose();

      if (!skipNavigation) {
        navigate(`/recruiting/jobs/${newJob.id}`);
      }
    } catch (error) {
      // Field errors paint red; the global mutation-error handler shows the toast.
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('jobs.createJob')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <Controller
          name="company_id"
          control={control}
          render={({ field }) => (
            <CompanyPicker
              value={field.value}
              onChange={field.onChange}
              label={t('common:multiCompany.hiringCompany', 'Hiring company')}
              required
            />
          )}
        />
        <div className="space-y-2">
          <Label htmlFor="title">{t('jobs.fields.title')} *</Label>
          <Controller
            name="title"
            control={control}
            rules={{ required: t('validation.titleRequired') }}
            render={({ field }) => (
              <Input
                {...field}
                id="title"
                autoFocus
                placeholder={t('jobs.titlePlaceholder')}
                className={cn(errors.title && 'border-destructive')}
              />
            )}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{errors.title.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">{t('jobs.fields.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="description"
                rows={3}
                placeholder={t('jobs.placeholders.description')}
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('jobs.fields.department')}</Label>
          <Controller
            name="department_id"
            control={control}
            render={({ field }) => (
              <DepartmentPicker
                value={field.value ?? ''}
                onChange={(id) => field.onChange(id || undefined)}
                allowClear
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('jobs.fields.locationType')}</Label>
          <Controller
            name="location_type"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`jobs.locationTypes.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {t('jobs.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
